import React, {
  Dispatch, SetStateAction, useEffect, useState,
} from 'react';
import {
  View, StyleSheet, TextInput, Text, ScrollView, FlatList,
  Pressable,
} from 'react-native';
import { useSelector } from 'react-redux';
import Colors from '../../../constants/Colors';
import { Building } from './AddBuildingModal';
import Style from '../../../constants/Style';
import { Area, Floor, Space } from '../../../components/Map/SetFloorsModal/FloorItem';
import { PressableState } from '../../../components/PressableStyled';
import SearchIcon from '../../../components/SearchIcon';

interface SearchPaneProps {
  searchTerm: string,
  setSearchTerm: Dispatch<SetStateAction<string>>,
  setSelectedFloorId: Dispatch<SetStateAction<string>>,
  setSelectedAreaId: Dispatch<SetStateAction<string>>,
  setSelectedSpaceId: Dispatch<SetStateAction<string>>,
  selectedFloor: Floor,
  selectedArea: Area,
  selectedSpace: Space,
}

type SearchResult = {
  id: string,
  name: string,
  floors: Floor[],
  areas: Area[],
  spaces: Space[],
}

const SearchPane = ({
  setSearchTerm, setSelectedFloorId, setSelectedAreaId, setSelectedSpaceId, selectedFloor,
  selectedArea, selectedSpace, searchTerm,
}: SearchPaneProps) => {
  const buildings: object = useSelector((state:
    { locations: { buildings } }) => state.locations.buildings);
  const searchString: string = (searchTerm ?? '').trim().toLowerCase();
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [viewedBuilding, setViewedBuilding] = useState<SearchResult>(null);

  useEffect(() => {
    setSearchResults(searchString.length > 0 ? Object.values(buildings ?? {})
      .map((building: Building) => {
        const floors: Floor[] = [];
        const areas: Area[] = [];
        const spaces: Space[] = [];
        Object.values(building?.floors ?? {})?.forEach((floor: Floor) => {
          if (floor && floor.Name.toLowerCase().includes(searchString)) floors.push(floor);
          Object.values(floor?.areas ?? {})?.forEach((area: Area) => {
            if (area && area.Name.toLowerCase().includes(searchString)) areas.push(area);
            Object.values(area?.spaces ?? {})?.forEach((space: Space) => {
              if (space && space.Name.toLowerCase().includes(searchString)) spaces.push(space);
            });
          });
        });
        return (floors.length > 0 || areas.length > 0 || spaces.length > 0) ? {
          id: building.LocationId,
          name: building.Name,
          floors: floors.sort((a: Floor, b: Floor) => a.Name.toLowerCase()
            .localeCompare(b.Name.toLowerCase())),
          areas: areas.sort((a: Area, b: Area) => a.Name.toLowerCase()
            .localeCompare(b.Name.toLowerCase())),
          spaces: spaces.sort((a: Space, b: Space) => a.Name.toLowerCase()
            .localeCompare(b.Name.toLowerCase())),
        } : null;
      }).filter((result: SearchResult) => !!result).sort((a: SearchResult, b: SearchResult) => a
        .name.toLowerCase().localeCompare(b.name.toLowerCase())) : []);
  }, [searchTerm, buildings]);

  useEffect(() => {
    setViewedBuilding(searchResults.filter((result: SearchResult) => result.id
      === viewedBuilding?.id)[0] ?? searchResults[0]);
  }, [searchResults]);

  return <View style={styles.wrapper}>
    <View style={styles.container}>
      <SearchIcon />
      <TextInput
        placeholder='Search'
        placeholderTextColor={Colors.light.hereworksBlack400}
        style={styles.inputText}
        value={searchTerm}
        onChangeText={setSearchTerm}
      />
    </View>
    <View>
      {searchResults.length > 0 && searchString.length > 0 && <ScrollView style={styles.list}>
        <FlatList
          horizontal
          data={searchResults}
          keyExtractor={(item: SearchResult) => `building_search_item_${item.id}`}
          renderItem={({ item }) => {
            const disabled: boolean = viewedBuilding?.id === item.id;
            return <Pressable
              style={({ hovered, pressed }: PressableState) => [
                styles.buildingItem, disabled && styles.buildingItemViewed,
                hovered && Style.listItemHovered, pressed && Style.listItemPressed,
              ]}
              {...{ disabled }}
              onPress={() => setViewedBuilding(item)}
            >
              <Text style={[styles.searchResultText, disabled && styles.viewedBuildingText]}>
                {item.name}
              </Text>
            </Pressable>;
          }}
          contentContainerStyle={styles.buildingList}
        />
        {viewedBuilding?.floors.length > 0 && <View style={styles.listHeader}>
          <Text style={styles.headerText}>{'FLOORS'}</Text>
        </View>}
        <FlatList
          data={viewedBuilding?.floors}
          renderItem={({ item }) => {
            const disabled: boolean = selectedFloor?.LocationId === item.LocationId;
            return <Pressable
              style={({ hovered, pressed }: PressableState) => [
                styles.searchResult, disabled && styles.currentlySelected,
                hovered && Style.listItemHovered, pressed && Style.listItemPressed,
              ]}
              {...{ disabled }}
              onPress={() => {
                setSelectedFloorId(item?.LocationId);
                setSelectedAreaId(null);
                setSelectedSpaceId(null);
              }}
            >
              <Text style={styles.searchResultText}>{item.Name}</Text>
              {disabled && <Text style={[styles.searchResultText, styles.currentSelectionText]}>
                {' (currently selected)'}
              </Text>}
            </Pressable>;
          }}
          keyExtractor={(floor: Floor) => `floor_${floor.LocationId}_from_${viewedBuilding?.id}`}
        />
        {viewedBuilding?.areas.length > 0 && <View style={styles.listHeader}>
          <Text style={styles.headerText}>{'AREAS'}</Text>
        </View>}
        <FlatList
          data={viewedBuilding?.areas}
          renderItem={({ item }) => {
            const disabled: boolean = selectedArea?.LocationId === item.LocationId;
            return <Pressable
              style={({ hovered, pressed }: PressableState) => [
                styles.searchResult, disabled && styles.currentlySelected,
                hovered && Style.listItemHovered, pressed && Style.listItemPressed,
              ]}
              {...{ disabled }}
              onPress={() => {
                setSelectedFloorId(item.ParentId);
                setSelectedAreaId(item.LocationId);
                setSelectedSpaceId(null);
              }}
            >
              <Text style={styles.searchResultText}>{item.Name}</Text>
              {disabled && <Text style={[styles.searchResultText, styles.currentSelectionText]}>
                {' (currently selected)'}
              </Text>}
            </Pressable>;
          }}
          keyExtractor={(floor: Floor) => `area_${floor.LocationId}_from_${viewedBuilding?.id}`}
        />
        {viewedBuilding?.spaces.length > 0 && <View style={styles.listHeader}>
          <Text style={styles.headerText}>{'SPACES'}</Text>
        </View>}
        <FlatList
          data={viewedBuilding?.spaces}
          renderItem={({ item }) => {
            const disabled: boolean = selectedSpace?.LocationId === item.LocationId;
            return <Pressable
              style={({ hovered, pressed }: PressableState) => [
                styles.searchResult, disabled && styles.currentlySelected,
                hovered && Style.listItemHovered, pressed && Style.listItemPressed,
              ]}
              {...{ disabled }}
              onPress={() => {
                setSelectedAreaId(item.ParentId);
                setSelectedSpaceId(item.LocationId);
              }}
            >
              <Text style={styles.searchResultText}>{item.Name}</Text>
              {disabled && <Text style={[styles.searchResultText, styles.currentSelectionText]}>
                {' (currently selected)'}
              </Text>}
            </Pressable>;
          }}
          keyExtractor={(floor: Floor) => `space_${floor.LocationId}_from_${viewedBuilding?.id}`}
        />
      </ScrollView>}
    </View>
  </View>;
};

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
  },
  container: {
    flex: 1,
    borderWidth: 1.5,
    borderRadius: 24,
    height: 40,
    flexDirection: 'row',
    alignItems: 'center',
    borderColor: Colors.light.hereworksBlue50,
    paddingHorizontal: 12,
  },
  inputText: {
    fontSize: 14,
    lineHeight: 16.41,
    color: Colors.light.hereworksBlack500,
    fontFamily: 'Roboto_400Regular',
    padding: 8,
    marginHorizontal: 4,
    flex: 1,
  },
  list: {
    position: 'absolute',
    overflow: 'hidden',
    top: 4,
    borderWidth: 1,
    borderRadius: 12,
    borderColor: Colors.light.hereworksBlue100,
    backgroundColor: '#FFF',
    width: '100%',
    maxHeight: 300,
    paddingVertical: 16,
  },
  buildingItem: {
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#FFF',
    borderWidth: 1.5,
    borderColor: Colors.light.hereworksWhite500,
    marginHorizontal: 4,
  },
  buildingItemViewed: {
    backgroundColor: Colors.light.hereworksDarkBlue,
  },
  buildingList: {
    paddingHorizontal: 12,
    marginBottom: 12,
  },
  listHeader: {
    ...Style.row,
    backgroundColor: Colors.light.hereworksWhite400,
    paddingHorizontal: 16,
    paddingVertical: 6,
  },
  headerText: {
    color: Colors.light.hereworksBlack500,
    fontSize: 12,
    lineHeight: 20,
    fontFamily: 'Roboto_500Medium',
  },
  searchResult: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 9,
  },
  searchResultText: {
    fontSize: 14,
    lineHeight: 21,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  viewedBuildingText: {
    color: '#FFF',
  },
  currentlySelected: {
    opacity: 0.5,
  },
  currentSelectionText: {
    fontStyle: 'italic',
    marginStart: 0,
  },
});

export default SearchPane;
